import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, ShieldCheck, Landmark, Coins } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

const HERO_IMG = "https://images.unsplash.com/photo-1487958449943-2429e8be8625?ixlib=rb-4.0.3&auto=format&fit=crop&w=1800&q=85";

const lineVariants = {
  hidden: { y: "110%", opacity: 0 },
  show: (i) => ({
    y: 0,
    opacity: 1,
    transition: { delay: 0.15 + i * 0.09, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

export function Hero() {
  const { t } = useI18n();

  const scrollTo = (hash) => (e) => {
    e.preventDefault();
    const el = document.querySelector(hash);
    if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
    else if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="inicio" className="relative min-h-[100svh] pt-24 lg:pt-28 pb-16 overflow-hidden grain" data-testid="hero-section">
      <div className="absolute inset-0 emerald-glow pointer-events-none" />

      {/* Background parallax image */}
      <motion.div
        className="absolute inset-0 z-0"
        initial={{ scale: 1.08, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.55 }}
        transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B132B]/70 via-[#0B132B]/85 to-[#0B132B]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B132B] via-transparent to-transparent" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.6 }}
          className="flex items-center gap-3"
        >
          <span className="inline-block h-px w-8 bg-emerald-500" />
          <p className="eyebrow">{t("hero.eyebrow")}</p>
        </motion.div>

        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight leading-[1.02] text-balance">
          {[t("hero.title_1"), t("hero.title_2"), t("hero.title_3")].map((line, i) => (
            <span key={i} className="hero-line-clip">
              <motion.span
                className="block"
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="show"
              >
                {i === 2 ? (
                  <span>
                    {line.split(" ").slice(0, -1).join(" ")}{" "}
                    <span className="text-emerald-400 italic font-light">
                      {line.split(" ").slice(-1)}
                    </span>
                  </span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.7 }}
          className="mt-8 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed"
        >
          {t("hero.subtitle")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.6 }}
          className="mt-10 flex flex-wrap items-center gap-3"
        >
          <a href="#proyectos" onClick={scrollTo("#proyectos")} className="btn-emerald text-sm" data-testid="hero-cta-primary">
            {t("hero.cta_primary")} <ArrowRight className="w-4 h-4" />
          </a>
          <a href="#como-funciona" onClick={scrollTo("#como-funciona")} className="btn-ghost text-sm" data-testid="hero-cta-secondary">
            {t("hero.cta_secondary")} <ArrowDown className="w-4 h-4" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl"
        >
          <MetaChip icon={ShieldCheck} label={t("hero.meta_1")} />
          <MetaChip icon={Landmark} label={t("hero.meta_2")} />
          <MetaChip icon={Coins} label={t("hero.meta_3")} />
        </motion.div>
      </div>
    </section>
  );
}

function MetaChip({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-800/80 bg-[#0F172A]/60 backdrop-blur-md px-4 py-3">
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-emerald-500/10 text-emerald-400">
        <Icon className="w-4 h-4" />
      </span>
      <span className="text-sm text-slate-200 font-mono tracking-wide">{label}</span>
    </div>
  );
}

export default Hero;
